import { useState, useEffect } from 'react'
import { X, Video, Settings, Shield, Users, Zap } from 'lucide-react'
import api from '../services/api'
import QuickAddForm from './QuickAddForm'
import AdvancedCameraForm from './AdvancedCameraForm'
import CameraModalFooter from './CameraModalFooter'

const CameraModal = ({ isOpen, onClose, camera = null, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'rtsp',
        url: '/Streaming/Channels/101',
        ipAddress: '',
        port: 554,
        username: '',
        password: '',
        location: {
            country: 'India',
            state: '',
            city: '',
            exactLocation: ''
        },
        coordinates: {
            longitude: '',
            latitude: ''
        },

        manufacturer: '',
        model: '',
        resolution: {
            width: 1920,
            height: 1080
        },
        fps: 25,
        bitrate: 2048,
        accessRoles: [],
        accessUsers: [],
        isActive: true
    })

    // Quick Add form data
    const [quickAddData, setQuickAddData] = useState({
        locationName: '',
        nvrIp: '',
        nvrPort: 554,
        nvrUsername: 'admin',
        nvrPassword: '',
        cameraCount: 1,
        startChannel: 101,
        channelIncrement: 100,
        accessRoles: []
    })

    const [roles, setRoles] = useState([])
    const [selectedUsers, setSelectedUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [showUserAssignment, setShowUserAssignment] = useState(false)
    const [isQuickAdd, setIsQuickAdd] = useState(true)

    const isEditing = !!camera

    useEffect(() => {
        if (isOpen) {
            fetchRoles()
            if (camera) {
                console.log('🔍 Camera data structure for editing:', camera);
                console.log('📍 Camera location data:', camera.location);
                console.log('🗺️ Camera coordinates field:', camera.coordinates);
                console.log('🌍 Camera longitude:', camera.coordinates?.longitude);
                console.log('🌍 Camera latitude:', camera.coordinates?.latitude);
                
                // When editing, use advanced form mode
                setIsQuickAdd(false)
                const isDahua = (camera.manufacturer || '').toLowerCase() === 'dahua';
                setFormData({
                    name: camera.name,
                    description: camera.description || '',
                    type: camera.type,
                    url: camera.url || (isDahua ? '/cam/realmonitor' : '/Streaming/Channels/101'),
                    trackNumber: camera.trackNumber ?? (isDahua ? 1 : 1001),
                    ipAddress: camera.ipAddress,
                    port: camera.port,
                    username: camera.username || '',
                    password: camera.password || '',
                    location: {
                        country: camera.location?.country || 'India',
                        state: camera.location?.state || '',
                        city: camera.location?.city || '',
                        exactLocation: camera.location?.exactLocation || ''
                    },
                    coordinates: {
                        longitude: camera.coordinates?.longitude || '',
                        latitude: camera.coordinates?.latitude || ''
                    },

                    manufacturer: camera.manufacturer || '',
                    dahuaPathType: camera.dahuaPathType || 'standard',
                    dahuaSubtype: camera.dahuaSubtype !== undefined && camera.dahuaSubtype !== null ? camera.dahuaSubtype : 0,
                    model: camera.model || '',
                    resolution: camera.resolution || { width: 1920, height: 1080 },
                    fps: camera.fps || 25,
                    bitrate: camera.bitrate || 2048,
                    accessRoles: camera.accessRoles?.map(role => role._id) || [],
                    accessUsers: camera.accessUsers?.map(user => user._id) || [],
                    isActive: camera.isActive
                })
                setSelectedUsers(camera.accessUsers || [])
            } else {
                // When adding new camera, default to quick add mode
                setIsQuickAdd(true)
                setFormData({
                    name: '',
                    description: '',
                    type: 'rtsp',
                    url: '/Streaming/Channels/101',
                    trackNumber: 1001,
                    ipAddress: '',
                    port: 554,
                    username: '',
                    password: '',
                    location: {
                        country: 'India',
                        state: '',
                        city: '',
                        exactLocation: ''
                    },
                    coordinates: {
                        longitude: '',
                        latitude: ''
                    },

                    manufacturer: '',
                    model: '',
                    resolution: { width: 1920, height: 1080 },
                    fps: 25,
                    bitrate: 2048,
                    accessRoles: [],
                    accessUsers: [],
                    isActive: true
                })
                setSelectedUsers([])
            }
            setErrors({})
            setShowAdvanced(false)
            setShowUserAssignment(false)
        }
    }, [isOpen, camera])

    const fetchRoles = async () => {
        try {
            const response = await api.get('/roles')
            setRoles(response.data.data.roles)
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const validateQuickAddForm = () => {
        const newErrors = {}

        if (!quickAddData.locationName.trim()) {
            newErrors.locationName = 'Location name is required'
        }

        if (!quickAddData.nvrIp.trim()) {
            newErrors.nvrIp = 'NVR IP address is required'
        } else if (!/^([a-zA-Z0-9.-]+)$/.test(quickAddData.nvrIp)) {
            newErrors.nvrIp = 'Please enter a valid IP address'
        }

        if (!quickAddData.nvrPort || quickAddData.nvrPort < 1 || quickAddData.nvrPort > 65535) {
            newErrors.nvrPort = 'Port must be between 1 and 65535'
        }

        if (!quickAddData.nvrUsername.trim()) {
            newErrors.nvrUsername = 'Username is required'
        }

        if (!quickAddData.nvrPassword.trim()) {
            newErrors.nvrPassword = 'Password is required'
        }

        if (!quickAddData.cameraCount || quickAddData.cameraCount < 1 || quickAddData.cameraCount > 50) {
            newErrors.cameraCount = 'Camera count must be between 1 and 50'
        }

        // Required custom role
        const customRole = roles.find(role => role.name.toLowerCase() === 'custom')
        if (customRole && !quickAddData.accessRoles.includes(customRole._id)) {
            newErrors.accessRoles = 'Custom role is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = 'Camera name is required'
        } else if (formData.name.length < 2) {
            newErrors.name = 'Camera name must be at least 2 characters'
        }

        if (!formData.url.trim()) {
            newErrors.url = 'Camera URL is required'
        }

        if (!formData.ipAddress.trim()) {
            newErrors.ipAddress = 'IP address or hostname is required'
        } else if (!/^([a-zA-Z0-9.-]+)$/.test(formData.ipAddress)) {
            newErrors.ipAddress = 'Please enter a valid IP address or hostname'
        }

        if (!formData.port || formData.port < 1 || formData.port > 65535) {
            newErrors.port = 'Port must be between 1 and 65535'
        }

        // Required username and password
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required'
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required'
        }

        // Required custom role
        const customRole = roles.find(role => role.name.toLowerCase() === 'custom')
        if (customRole && !formData.accessRoles.includes(customRole._id)) {
            newErrors.accessRoles = 'Custom role is required'
        }

        if (formData.resolution.width < 320 || formData.resolution.width > 7680) {
            newErrors.resolutionWidth = 'Resolution width must be between 320 and 7680'
        }

        if (formData.resolution.height < 240 || formData.resolution.height > 4320) {
            newErrors.resolutionHeight = 'Resolution height must be between 240 and 4320'
        }

        if (formData.fps < 1 || formData.fps > 60) {
            newErrors.fps = 'FPS must be between 1 and 60'
        }

        if (formData.bitrate < 64 || formData.bitrate > 8192) {
            newErrors.bitrate = 'Bitrate must be between 64 and 8192'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) return

        setLoading(true)
        try {
            const submitData = { ...formData }
            console.log('📤 Submitting camera data:', submitData);
            console.log('📍 Location data being sent:', submitData.location);
            console.log('🗺️ Coordinates data being sent:', submitData.coordinates);
            console.log('🌍 Longitude being sent:', submitData.coordinates?.longitude);
            console.log('🌍 Latitude being sent:', submitData.coordinates?.latitude);

            let response
            if (isEditing) {
                response = await api.put(`/cameras/${camera._id}`, submitData)
            } else {
                response = await api.post('/cameras', submitData)
            }

            // Pass the updated camera data back to the parent
            onSuccess(response.data.data?.camera)
            onClose()
            
            // Log successful camera save for debugging
            console.log('Camera saved successfully:', response.data.data?.camera?.name || 'New camera')
        } catch (error) {
            console.error('Error saving camera:', error)
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        } finally {
            setLoading(false)
        }
    }

    const handleQuickAddSubmit = async (e) => {
        e.preventDefault()

        if (!validateQuickAddForm()) return

        setLoading(true)
        try {
            const cameras = []

            for (let i = 0; i < quickAddData.cameraCount; i++) {
                const channelNumber = quickAddData.startChannel + (i * quickAddData.channelIncrement)
                const cameraData = {
                    name: `${quickAddData.locationName} Camera ${channelNumber}`,
                    description: `Camera ${channelNumber} at ${quickAddData.locationName}`,
                    type: 'rtsp',
                    url: `/Streaming/Channels/${channelNumber}`,
                    trackNumber: channelNumber, // Set track number to match channel number
                    ipAddress: quickAddData.nvrIp,
                    port: quickAddData.nvrPort,
                    username: quickAddData.nvrUsername,
                    password: quickAddData.nvrPassword,
                    location: {
                        country: 'India',
                        state: '',
                        city: '',
                        exactLocation: quickAddData.locationName
                    },
                    coordinates: {
                        longitude: '',
                        latitude: ''
                    },
                    manufacturer: 'Hikvision',
                    model: 'Auto-detected',
                    resolution: { width: 1920, height: 1080 },
                    fps: 25,
                    bitrate: 2048,
                    accessRoles: quickAddData.accessRoles,
                    accessUsers: [],
                    isActive: true
                }
                cameras.push(cameraData)
            }

            // Create all cameras
            const promises = cameras.map(cameraData => api.post('/cameras', cameraData))
            await Promise.all(promises)

            onSuccess()
            onClose()
        } catch (error) {
            console.error('Error creating cameras:', error)
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    const handleQuickAddChange = (e) => {
        const { name, value, type, checked } = e.target
        setQuickAddData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    const handleLocationChange = (field, value) => {
        // Check if this is a coordinate field or location field
        if (field === 'longitude' || field === 'latitude') {
            setFormData(prev => ({
                ...prev,
                coordinates: {
                    ...prev.coordinates,
                    [field]: value
                }
            }))
            
            if (errors[`coordinates.${field}`]) {
                setErrors(prev => ({
                    ...prev,
                    [`coordinates.${field}`]: ''
                }))
            }
        } else {
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value
                }
            }))

            if (errors[`location.${field}`]) {
                setErrors(prev => ({
                    ...prev,
                    [`location.${field}`]: ''
                }))
            }
        }
    }

    const handleResolutionChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            resolution: {
                ...prev.resolution,
                [field]: parseInt(value)
            }
        }))

        if (errors[`resolution${field.charAt(0).toUpperCase() + field.slice(1)}`]) {
            setErrors(prev => ({
                ...prev,
                [`resolution${field.charAt(0).toUpperCase() + field.slice(1)}`]: ''
            }))
        }
    }

    const handleRoleChange = (roleId) => {
        setFormData(prev => ({
            ...prev,
            accessRoles: prev.accessRoles.includes(roleId)
                ? prev.accessRoles.filter(id => id !== roleId)
                : [...prev.accessRoles, roleId]
        }))

        // Clear access roles error when user selects custom role
        if (errors.accessRoles) {
            setErrors(prev => ({ ...prev, accessRoles: '' }))
        }
    }

    const handleQuickAddRoleChange = (roleId) => {
        setQuickAddData(prev => ({
            ...prev,
            accessRoles: prev.accessRoles.includes(roleId)
                ? prev.accessRoles.filter(id => id !== roleId)
                : [...prev.accessRoles, roleId]
        }))

        // Clear access roles error when user selects custom role
        if (errors.accessRoles) {
            setErrors(prev => ({ ...prev, accessRoles: '' }))
        }
    }

    const handleUsersChange = (users) => {
        setSelectedUsers(users)
        setFormData(prev => ({
            ...prev,
            accessUsers: users.map(user => user._id)
        }))
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Video className="h-6 w-6 text-indigo-600" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {isEditing ? 'Edit Camera' : 'Add New Camera'}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {isEditing ? 'Update camera settings and access control' : 'Configure camera settings and assign access'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Mode Toggle (only for new camera) */}
                {!isEditing && (
                    <div className="flex items-center justify-center p-4 bg-blue-50 border-b border-blue-200">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setIsQuickAdd(false)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    !isQuickAdd 
                                        ? 'bg-indigo-600 text-white' 
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <Settings className="h-4 w-4 inline mr-2" />
                                Advanced Mode
                            </button>
                            <button
                                onClick={() => setIsQuickAdd(true)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    isQuickAdd 
                                        ? 'bg-green-600 text-white' 
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <Zap className="h-4 w-4 inline mr-2" />
                                Quick Add
                            </button>
                        </div>
                    </div>
                )}

                {/* Form Content */}
                <form onSubmit={isQuickAdd ? handleQuickAddSubmit : handleSubmit} className="flex flex-col" style={{ height: 'calc(90vh - 180px)' }}>
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 240px)' }}>
                        {isQuickAdd ? (
                            <QuickAddForm
                                quickAddData={quickAddData}
                                handleQuickAddChange={handleQuickAddChange}
                                handleQuickAddRoleChange={handleQuickAddRoleChange}
                                roles={roles}
                                errors={errors}
                            />
                        ) : (
                            <AdvancedCameraForm
                                formData={formData}
                                handleChange={handleChange}
                                handleLocationChange={handleLocationChange}
                                handleResolutionChange={handleResolutionChange}
                                handleRoleChange={handleRoleChange}
                                handleUsersChange={handleUsersChange}
                                roles={roles}
                                selectedUsers={selectedUsers}
                                showUserAssignment={showUserAssignment}
                                setShowUserAssignment={setShowUserAssignment}
                                errors={errors}
                            />
                        )}
                    </div>

                    {/* Fixed Footer */}
                    <CameraModalFooter
                        isQuickAdd={isQuickAdd}
                        isEditing={isEditing}
                        loading={loading}
                        quickAddData={quickAddData}
                        formData={formData}
                        handleChange={handleChange}
                        onClose={onClose}
                    />
                </form>
            </div>
        </div>
    )
}

export default CameraModal 